import React, { useEffect } from 'react';
import { Box } from '@mui/material';

/**
 * Realistic miniature resume preview for a template.
 * Draws a tiny mock resume using the template's real colors and heading font,
 * laid out per layout engine. Loads the heading Google Font for fidelity.
 */

const SAMPLE_NAME = 'Alex Morgan';
const SAMPLE_TITLE = 'Product Designer';

const loadedFonts = new Set();

function useTemplateFont(fonts) {
  useEffect(() => {
    if (!fonts?.heading) return;
    const family = fonts.heading.split(',')[0].replace(/["']/g, '').trim();
    if (!family || loadedFonts.has(family)) return;
    loadedFonts.add(family);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
  }, [fonts?.heading]);
}

const Line = ({ w = '100%', h = 3, c = 'rgba(0,0,0,0.14)', mb = 3, br = 2 }) => (
  <Box sx={{ width: w, height: `${h}px`, bgcolor: c, mb: `${mb}px`, borderRadius: `${br}px`, flexShrink: 0 }} />
);

const SectionTitle = ({ colors, fonts, children, small, filled }) => (
  <Box
    sx={{
      fontFamily: fonts.heading,
      fontWeight: 700,
      fontSize: small ? 5 : 6,
      letterSpacing: filled ? '0.04em' : '0.06em',
      textTransform: 'uppercase',
      color: filled ? '#fff' : colors.primary,
      bgcolor: filled ? colors.primary : 'transparent',
      borderBottom: filled ? 'none' : `1px solid ${colors.accent}`,
      borderRadius: filled ? '2px' : 0,
      px: filled ? '4px' : 0,
      py: filled ? '2px' : 0,
      pb: filled ? '2px' : '2px',
      mb: '4px',
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

const Name = ({ colors, fonts, size = 12, color, align = 'left' }) => (
  <Box
    sx={{
      fontFamily: fonts.heading,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 1.2,
      color: color || colors.primary,
      textAlign: align,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    }}
  >
    {SAMPLE_NAME}
  </Box>
);

const Subtitle = ({ colors, size = 6, align = 'left' }) => (
  <Box
    sx={{
      fontSize: size,
      color: colors.secondary,
      textAlign: align,
      lineHeight: 1.5,
      mb: '4px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    }}
  >
    {SAMPLE_TITLE}
  </Box>
);

const bodyLine = 'rgba(0,0,0,0.13)';
const bodyLineSoft = 'rgba(0,0,0,0.08)';

const TextBlock = ({ lines = 3, softLast = true }) => (
  <Box sx={{ mb: '5px' }}>
    {Array.from({ length: lines }).map((_, i) => (
      <Line
        key={i}
        w={`${100 - (i === lines - 1 ? 32 : i * 6)}%`}
        c={softLast && i === lines - 1 ? bodyLineSoft : bodyLine}
        h={2.5}
        mb={2.5}
      />
    ))}
  </Box>
);

const ExperienceEntry = ({ colors }) => (
  <Box sx={{ mb: '5px' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
      <Line w="42%" h={3.5} c={colors.primary} mb={0} />
      <Line w="16%" h={2.5} c={bodyLineSoft} mb={0} />
    </Box>
    <Line w="30%" h={2.5} c={colors.secondary} mb={3} />
    <Line w="94%" h={2.5} c={bodyLine} mb={2.5} />
    <Line w="86%" h={2.5} c={bodyLine} mb={2.5} />
  </Box>
);

const SkillChips = ({ colors, count = 5 }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: `${18 + (i % 3) * 6}%`,
          height: 6,
          borderRadius: 3,
          bgcolor: colors.light === '#FFFFFF' || colors.light === '#ffffff' ? 'rgba(0,0,0,0.06)' : colors.light,
          border: `0.5px solid ${colors.accent}`,
        }}
      />
    ))}
  </Box>
);

const SingleColumnMini = ({ colors, fonts }) => (
  <Box sx={{ p: '10px 12px', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ textAlign: 'center', borderBottom: `1.5px solid ${colors.primary}`, pb: '5px', mb: '6px' }}>
      <Name colors={colors} fonts={fonts} align="center" size={11} />
      <Subtitle colors={colors} align="center" size={5.5} />
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
        <Line w="18%" h={2} c={bodyLineSoft} mb={0} />
        <Line w="14%" h={2} c={bodyLineSoft} mb={0} />
        <Line w="16%" h={2} c={bodyLineSoft} mb={0} />
      </Box>
    </Box>
    <SectionTitle colors={colors} fonts={fonts}>Summary</SectionTitle>
    <TextBlock lines={2} />
    <SectionTitle colors={colors} fonts={fonts}>Experience</SectionTitle>
    <ExperienceEntry colors={colors} />
    <ExperienceEntry colors={colors} />
    <SectionTitle colors={colors} fonts={fonts}>Skills</SectionTitle>
    <SkillChips colors={colors} />
  </Box>
);

const TwoColumnMini = ({ colors, fonts }) => (
  <Box sx={{ height: '100%', display: 'flex' }}>
    <Box sx={{ width: '34%', bgcolor: colors.sidebar, p: '10px 7px', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: 22, height: 22, borderRadius: '50%',
          bgcolor: colors.primary, opacity: 0.85, mb: '6px', mx: 'auto',
        }}
      />
      <SectionTitle colors={colors} fonts={fonts} small>Contact</SectionTitle>
      <Line w="90%" h={2} c="rgba(0,0,0,0.2)" mb={2.5} />
      <Line w="75%" h={2} c="rgba(0,0,0,0.2)" mb={2.5} />
      <Line w="82%" h={2} c="rgba(0,0,0,0.2)" mb={6} />
      <SectionTitle colors={colors} fonts={fonts} small>Skills</SectionTitle>
      {[88, 70, 80, 62].map((w, i) => (
        <Line key={i} w={`${w}%`} h={2.5} c={colors.primary} mb={3} br={2} />
      ))}
      <SectionTitle colors={colors} fonts={fonts} small>Education</SectionTitle>
      <Line w="90%" h={2} c="rgba(0,0,0,0.2)" mb={2.5} />
      <Line w="70%" h={2} c="rgba(0,0,0,0.2)" mb={0} />
    </Box>
    <Box sx={{ flex: 1, p: '10px 9px' }}>
      <Name colors={colors} fonts={fonts} size={10} />
      <Subtitle colors={colors} size={5.5} />
      <SectionTitle colors={colors} fonts={fonts}>Profile</SectionTitle>
      <TextBlock lines={2} />
      <SectionTitle colors={colors} fonts={fonts}>Experience</SectionTitle>
      <ExperienceEntry colors={colors} />
      <ExperienceEntry colors={colors} />
    </Box>
  </Box>
);

const TimelineMini = ({ colors, fonts }) => {
  const rail = colors.timeline || colors.accent;
  return (
    <Box sx={{ p: '10px 12px', height: '100%' }}>
      <Box sx={{ bgcolor: colors.light, borderRadius: '4px', p: '6px 8px', mb: '6px', textAlign: 'center' }}>
        <Name colors={colors} fonts={fonts} size={10} align="center" color={colors.text === '#EAEAEA' ? '#fff' : colors.primary} />
        <Subtitle colors={colors} size={5} align="center" />
      </Box>
      <SectionTitle colors={colors} fonts={fonts}>Career Journey</SectionTitle>
      <Box sx={{ display: 'flex', gap: '6px' }}>
        <Box sx={{ width: 2.5, bgcolor: rail, borderRadius: 2, position: 'relative', flexShrink: 0 }}>
          {[4, 42, 80].map((top) => (
            <Box
              key={top}
              sx={{
                position: 'absolute', top: `${top}%`, left: '50%',
                transform: 'translateX(-50%)',
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: rail, border: '1px solid #fff',
              }}
            />
          ))}
        </Box>
        <Box sx={{ flex: 1 }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ mb: '5px', bgcolor: colors.light, borderRadius: '3px', p: '4px 5px' }}>
              <Line w="46%" h={3} c={colors.primary} mb={2.5} />
              <Line w="90%" h={2.5} c={bodyLine} mb={2.5} />
              <Line w="72%" h={2.5} c={bodyLineSoft} mb={0} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const SplitMini = ({ colors, fonts }) => (
  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ bgcolor: colors.header || colors.light, p: '9px 12px 7px', borderBottom: `2px solid ${colors.primary}` }}>
      <Name colors={colors} fonts={fonts} size={11} />
      <Subtitle colors={colors} size={5.5} />
      <Box sx={{ display: 'flex', gap: '4px' }}>
        <Line w="16%" h={2} c={colors.secondary} mb={0} />
        <Line w="13%" h={2} c={colors.secondary} mb={0} />
        <Line w="15%" h={2} c={colors.secondary} mb={0} />
      </Box>
    </Box>
    <Box sx={{ flex: 1, display: 'flex', gap: '8px', p: '8px 12px' }}>
      <Box sx={{ flex: 1.5 }}>
        <SectionTitle colors={colors} fonts={fonts} filled>Experience</SectionTitle>
        <ExperienceEntry colors={colors} />
        <ExperienceEntry colors={colors} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <SectionTitle colors={colors} fonts={fonts} small filled>Skills</SectionTitle>
        {[90, 72, 84, 60].map((w, i) => (
          <Line key={i} w={`${w}%`} h={2.5} c={colors.accent} mb={3} />
        ))}
        <SectionTitle colors={colors} fonts={fonts} small filled>Education</SectionTitle>
        <Line w="88%" h={2.5} c={bodyLine} mb={2.5} />
        <Line w="66%" h={2.5} c={bodyLineSoft} mb={0} />
      </Box>
    </Box>
  </Box>
);

const CompactMini = ({ colors, fonts }) => (
  <Box sx={{ p: '9px 11px', height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: `1px solid ${colors.primary}`, pb: '3px', mb: '4px' }}>
      <Name colors={colors} fonts={fonts} size={9.5} />
      <Line w="26%" h={2} c={bodyLineSoft} mb={0} />
    </Box>
    <SectionTitle colors={colors} fonts={fonts} small filled>Summary</SectionTitle>
    <Line w="98%" h={2} c={bodyLine} mb={2} />
    <Line w="92%" h={2} c={bodyLineSoft} mb={4} />
    <SectionTitle colors={colors} fonts={fonts} small filled>Experience</SectionTitle>
    {[0, 1, 2].map((i) => (
      <Box key={i} sx={{ mb: '3px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '1.5px' }}>
          <Line w="38%" h={2.5} c={colors.primary} mb={0} />
          <Line w="14%" h={2} c={bodyLineSoft} mb={0} />
        </Box>
        <Line w="96%" h={2} c={bodyLine} mb={1.5} />
        <Line w="88%" h={2} c={bodyLineSoft} mb={0} />
      </Box>
    ))}
    <Box sx={{ display: 'flex', gap: '8px', mt: '4px' }}>
      <Box sx={{ flex: 1 }}>
        <SectionTitle colors={colors} fonts={fonts} small filled>Skills</SectionTitle>
        <Line w="90%" h={2} c={bodyLine} mb={2} />
        <Line w="76%" h={2} c={bodyLine} mb={0} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <SectionTitle colors={colors} fonts={fonts} small filled>Education</SectionTitle>
        <Line w="88%" h={2} c={bodyLine} mb={2} />
        <Line w="64%" h={2} c={bodyLineSoft} mb={0} />
      </Box>
    </Box>
  </Box>
);

const LAYOUTS = {
  'single-column': SingleColumnMini,
  'two-column': TwoColumnMini,
  timeline: TimelineMini,
  split: SplitMini,
  compact: CompactMini,
};

function isDarkTemplate(colors) {
  const light = (colors.light || '').toLowerCase();
  const text = (colors.text || '').toLowerCase();
  return light === '#1e1e1e' || light === '#242424' || text === '#eaeaea' || text === '#f5f5f5';
}

/** Internal render width before CSS scale-down (compact picker thumbs). */
const COMPACT_RENDER_WIDTH = 144;
const COMPACT_DISPLAY_WIDTH = 48;
const PAGE_ASPECT = 260 / 210;

const thumbnailSurfaceSx = (dark, sx = {}) => ({
  bgcolor: dark ? '#1a1a1a' : '#ffffff',
  borderRadius: 1.5,
  overflow: 'hidden',
  boxShadow: dark
    ? '0 4px 14px rgba(0,0,0,0.35)'
    : '0 4px 16px rgba(15, 23, 42, 0.12)',
  border: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15, 23, 42, 0.08)',
  userSelect: 'none',
  pointerEvents: 'none',
  ...sx,
});

const TemplateThumbnail = ({ template, sx, compact = false }) => {
  useTemplateFont(template?.fonts);
  const Layout = LAYOUTS[template.layout] || SingleColumnMini;
  const dark = isDarkTemplate(template.colors);

  const page = (
    <Layout colors={template.colors} fonts={template.fonts} />
  );

  if (compact) {
    const displayWidth = COMPACT_DISPLAY_WIDTH;
    const displayHeight = Math.round(displayWidth * PAGE_ASPECT);
    const renderWidth = COMPACT_RENDER_WIDTH;
    const renderHeight = renderWidth * PAGE_ASPECT;
    const scale = displayWidth / renderWidth;

    return (
      <Box
        sx={{
          width: displayWidth,
          height: displayHeight,
          flexShrink: 0,
          overflow: 'hidden',
          ...thumbnailSurfaceSx(dark, {
            borderRadius: 1,
            boxShadow: dark
              ? '0 2px 8px rgba(0,0,0,0.3)'
              : '0 2px 8px rgba(15, 23, 42, 0.1)',
            ...sx,
          }),
        }}
      >
        <Box
          sx={{
            width: renderWidth,
            height: renderHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            ...thumbnailSurfaceSx(dark, {
              width: renderWidth,
              height: renderHeight,
              borderRadius: 0,
              boxShadow: 'none',
              border: 'none',
            }),
          }}
        >
          {page}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        aspectRatio: '210 / 260',
        width: '100%',
        ...thumbnailSurfaceSx(dark, sx),
      }}
    >
      {page}
    </Box>
  );
};

export default TemplateThumbnail;
